import { useEffect, useState } from "react";
import { Plus, Trash2, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VideoPlayer } from "@/lib/video";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState({ title: "", video_url: "", caption: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/lot-videos").then((r) => setVideos(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/lot-videos", { ...form, caption: form.caption || null });
      toast.success("Video posted — it's live on your homepage");
      setForm({ title: "", video_url: "", caption: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't post video");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Remove "${v.title}"?`)) return;
    try {
      await api.delete(`/lot-videos/${v.id}`);
      toast.success("Video removed");
      load();
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  return (
    <div data-testid="admin-videos">
      <h2 className="font-serif text-3xl text-primary">Lot Video Board</h2>
      <p className="text-sm text-muted-foreground">
        Post quick updates from the lot — new arrivals, walkarounds, a hello from Kendall. They show in the
        &ldquo;From the Lot&rdquo; section on your homepage. Tip: upload to YouTube from your phone, then paste the link here.
      </p>

      <form onSubmit={add} className="mt-6 border border-soft bg-card p-5 grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="lv-title">Title *</Label>
          <Input id="lv-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Just in: 2016 Honda Odyssey" data-testid="video-title-input" />
        </div>
        <div>
          <Label htmlFor="lv-url">YouTube or MP4 link *</Label>
          <Input id="lv-url" required value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtu.be/…" data-testid="video-url-input" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="lv-cap">Caption (optional)</Label>
          <Input id="lv-cap" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="Fresh detail, new tires, ready for a family." data-testid="video-caption-input" />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={busy} className="rounded-sm gap-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="video-post-btn">
            <Plus className="h-4 w-4" /> {busy ? "Posting…" : "Post Video"}
          </Button>
        </div>
      </form>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {videos.length === 0 && (
          <div className="sm:col-span-2 lg:col-span-3 border border-dashed border-soft p-10 text-center text-muted-foreground" data-testid="videos-empty">
            <Video className="h-6 w-6 mx-auto opacity-40" />
            <p className="mt-2 text-sm">No videos yet — post your first lot update above.</p>
          </div>
        )}
        {videos.map((v) => (
          <div key={v.id} className="border border-soft bg-card" data-testid={`video-row-${v.id}`}>
            <div className="aspect-video bg-black">
              <VideoPlayer url={v.video_url} title={v.title} testId={`video-player-${v.id}`} />
            </div>
            <div className="p-4 flex items-start justify-between gap-2">
              <div>
                <p className="font-medium text-primary text-sm">{v.title}</p>
                {v.caption && <p className="text-xs text-muted-foreground mt-0.5">{v.caption}</p>}
                <p className="text-[10px] text-muted-foreground mt-1">{new Date(v.created_at).toLocaleDateString()}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(v)} title="Remove" data-testid={`delete-video-${v.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
